import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  Row,
} from "reactstrap";
import BreadCrumb from "../../components/common/breadCrumb";
import { useFormik } from "formik";
import * as Yup from "yup";
import { InputPlaceHolder, validationMessages } from "../../utils/helpers/validationMasseges";
import TableContainer from "../../components/common/tableContainer";
import DeleteModal from "../../components/common/deleteModal";
import { Tooltip as ReactTooltip } from "react-tooltip";
import Loader from "../../components/common/loader";
import { listOfData } from "../../api/commonApi";
import {
  addCompanyDetails,
  deleteCompanyDetails,
  editCompanyDetails,
} from "../../api/companyApi";
import { ResponseStatusEnum } from "../../utils/helpers/enums";
import { toast } from "react-toastify";
import NoResultsMessage from "../../components/noResultMessage";
import { Link, useNavigate } from "react-router-dom";
import { getId } from "../../utils/helpers/custom";
import { BdeLabel } from "../../common/constant/Bde";
import BaseButton from "../../components/BaseComponents/BaseButton";
import { checkStatusCodeSuccess, CommonLabel } from "../../utils/helpers/common";
import BaseInput from "../../components/BaseComponents/BaseInput";

const CompanyManagement = () => {
  document.title = BdeLabel.CompanyInformation;
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableLoader, setTableLoader] = useState(false);
  const [listOfCompanyData, setListOfCompanyData] = useState();
  const [editCompanyData, setEditCompanyData] = useState();
  const [selectedCompanyId, setSelectedCompanyId] = useState();
  const authId = getId();
  const navigate = useNavigate();

  const fetchData = () => {
    setTableLoader(true);
    listOfData({
      modelName: "companyModel",
      condition: { isDeleted: 0 },
      selectionCriteria: ["id", "companyName"],
    })
      .then((res) => {
        setListOfCompanyData(res?.data);
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setTableLoader(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const projectColumns = useMemo(() => [
    {
      Header: CommonLabel.SrNo,
      Cell: ({ row }) => {
        const indexFromStart = row?.index + 1;
        return <div>{indexFromStart}</div>;
      },
    },
    {
      Header: BdeLabel.CompanyName,
      accessor: "companyName",
      filterable: false,
    },
    {
      Header: CommonLabel.Action,
      Cell: (cellProps) => {
        return (
          <div>
            <div className="hstack gap-2">
              <Link
                id={`editMode-${cellProps?.cell?.row?.index}`}
                className="btn btn-sm btn-soft-info remove-list"
                onClick={() =>
                  editCompanyDetailsClick(cellProps?.cell?.row?.original)
                }>
                <i className="ri-pencil-fill align-bottom" />
              </Link>
                <ReactTooltip
                  place="bottom"
                  variant="info"
                  content="Edit"
                  anchorId={`editMode-${cellProps?.cell?.row?.index}`}
                />
              <Link
                id={`deleteMode-${cellProps.cell.row.index}`}
                className="btn btn-sm btn-soft-danger remove-list"
                onClick={() =>
                  companyDelete(cellProps?.cell?.row?.original?.id)
                }>
                <i className="ri-delete-bin-5-fill align-bottom" />
              </Link>
                <ReactTooltip
                  place="bottom"
                  variant="error"
                  content="Delete"
                  anchorId={`deleteMode-${cellProps?.cell?.row?.index}`}
                />
            </div>
          </div>
        );
      },
    },
  ]);

  const editCompanyDetailsClick = (cellProps) => {
    setEditCompanyData(cellProps);
    setIsEdit(true);
    navigate("/company-management/");
  };

  const companyDelete = (companyId) => {
    setSelectedCompanyId(companyId);
    setDeleteModal(true);
  };

  const companyDeleteTodo = () => {
    setLoading(true);
    setDeleteModal(false);
    deleteCompanyDetails(selectedCompanyId, { isDeleted: true })
      .then((res) => {
        if (res?.data?.statusCode === ResponseStatusEnum.SUCCESS) {
          fetchData();
          toast.success(res?.data?.message);
        } else {
          toast.error(res?.data?.message);
        }
      })
      .catch((err) => {
        return err;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      companyName: editCompanyData?.companyName || "",
    },
    validationSchema: Yup.object({
      companyName: Yup.string().required(validationMessages.required(BdeLabel.CompanyName)),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        setLoading(true);
        setIsEdit(false);
        setEditCompanyData("");
        editCompanyDetails(editCompanyData?.id, { ...values })
          .then((res) => {
            if (checkStatusCodeSuccess(res?.data?.statusCode)) {
              fetchData();
              validation.resetForm();
              toast.success(res?.data?.message);
            } else {
              toast.error(res?.data?.message);
            }
          })
          .catch((err) => {
            return err;
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setLoading(true);
        addCompanyDetails({ ...values, userId: authId })
          .then((res) => {
            if (checkStatusCodeSuccess(res?.data?.statusCode)) {
              fetchData();
              toast.success(res?.data?.message);
            } else {
              toast.error(res?.data?.message);
            }
          })
          .catch((err) => {
            return err;
          })
          .finally(() => {
            setLoading(false);
            validation.resetForm();
          });
      }
    },
  });

  return (
    <>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => companyDeleteTodo()}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        {loading === false ? (
          <Container fluid>
            <BreadCrumb title={BdeLabel.CompanyInformation} pageTitle="HRMS" />
            <Row className="mb-2">
              <div className="col-sm order-3 order-sm-2 mt-3 mt-sm-0">
                <h5 className="fw-semibold mb-0 mt-2">
                  {isEdit === false ? CommonLabel.Add : CommonLabel.Update}{" "}
                  {BdeLabel.CompanyInformation}
                </h5>
              </div>
              {isEdit && (
                <div className="col-auto order-2 order-sm-3 ms-auto">
                  <div className="hstack gap-2">
                    <BaseButton
                      color="primary"
                      onClick={() => {
                        setIsEdit(false);
                        setEditCompanyData("");
                      }}
                    >
                      {CommonLabel.Back}
                    </BaseButton>
                  </div>
                </div>
              )}
              <div className="col-auto order-2 order-sm-3 ms-auto">
                <div className="hstack gap-2">
                  <Link to="/project-management">
                    <BaseButton color="primary" type="button">
                      + {BdeLabel.Client}
                    </BaseButton>
                  </Link>
                </div>
              </div>
            </Row>

            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <Card>
                <CardBody>
                  <Row>
                    <Col lg={6}>
                      <div className="mb-3">
                        <BaseInput
                          name="companyName"
                          type="text"
                          placeholder={InputPlaceHolder(BdeLabel.CompanyName)}
                          label={BdeLabel.CompanyName}
                          onChange={validation.handleChange}
                          handleBlur={validation.handleBlur}
                          value={validation.values.companyName}
                          touched={validation.touched.companyName}
                          error={validation.errors.companyName}
                          required
                        />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="hstack gap-2 justify-content-end">
                        <BaseButton type="submit" color="primary">
                          {isEdit === false
                            ? CommonLabel.Submit
                            : CommonLabel.Update}
                        </BaseButton>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Form>

            <Card>
              <CardHeader className="align-items-center d-flex">
                <h4 className="card-title mb-0 flex-grow-1">
                  {BdeLabel.Company}
                </h4>
              </CardHeader>
              <CardBody>
                {(listOfCompanyData?.length || tableLoader) ? (
                  <TableContainer
                    columns={projectColumns}
                    data={listOfCompanyData || []}
                    isAddUserList={false}
                    customPageSize={5}
                    isGlobalFilter={true}
                    tableLoader={tableLoader}
                    divClass="table-responsive table-card mb-1"
                    tableClass="align-middle table-nowrap"
                    theadClass="table-light text-muted"
                  />
                ) : (
                  <NoResultsMessage />
                )}
              </CardBody>
            </Card>
          </Container>
        ) : (
          <Loader />
        )}
      </div>
    </>
  );
};

export default CompanyManagement;
